<template>
  <div :class="load ? 'd-flex' : ''">
    <div class="pt-2" v-show="!load">
      <div v-if="info">
        <!-- Histórico de leitura header -->
        <div class="na-historico-header">
          <span class="na-historico-title">Previsão do tempo</span>
        </div>

        <!-- Day items + expanded content connected -->
        <div class="na-days-container">
          <div class="na-days-row">
            <NewAsideDayItem
              v-for="(d, i) in info.days"
              :key="i"
              :dayData="d"
              :index="i"
              :isActive="expandedDayIndex === i"
              stationType="clima"
              @select="onDaySelect"
            />
          </div>

          <!-- EXPANDED DAY CONTENT -->
          <div v-if="expandedDayIndex !== null && hour" class="na-expanded-content" :style="expandedContentRadius">

            <!-- Astronomia -->
            <div class="na-section na-astronomia" v-if="currentDayAstronomia">
              <div class="na-astro-label">Astronomia</div>
              <div class="na-astro-row">
                <div class="na-astro-item">
                  <div class="na-astro-sun"></div>
                  <div class="na-astro-info">
                    <b>Sol</b>
                    <span>nasce / {{ formatHourMinute(currentDayAstronomia.sol_nasce) }}</span>
                    <span>poe / {{ formatHourMinute(currentDayAstronomia.sol_poe) }}</span>
                  </div>
                </div>
                <div class="na-astro-item">
                  <img v-if="luaFaseIcon" :src="luaFaseIcon" class="na-astro-moon" alt="Fase da lua" />
                  <div v-else class="na-astro-moon"></div>
                  <div class="na-astro-info">
                    <b>Lua</b>
                    <span>{{ luaFaseLabel }}</span>
                    <span>nasce / {{ formatHourMinute(currentDayAstronomia.lua_nace) }}</span>
                    <span>poe / {{ formatHourMinute(currentDayAstronomia.lua_poe) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Previsão para -->
            <div class="na-section na-leitura">
              <div class="na-leitura-row">
                <span class="na-leitura-label">Previsão para</span>
                <span class="na-leitura-date">{{ formatDate(hour.data) }}</span>
                <span class="na-leitura-time">{{ formatTime(hour.data) }}</span>
              </div>
            </div>

            <!-- Temperatura -->
            <div :class="['na-section', 'na-temp-card', 'na-clickable', { 'na-card-selected': selectedCardKey === 'temperatura' }]"
              @click="selectCard('temperatura')">
              <div class="na-temp-header">
                <img src="..\mapa\src\components\mapa\stationIcon\TemperaturaBlack.svg" class="na-icon-20" alt="temp" />
                <span>Temperatura</span>
              </div>
              <div class="na-temp-body" :style="selectedCardKey !== 'temperatura' ? { background: `linear-gradient(90deg, rgba(${getTemperatureColor()}, 0.12) 0%, rgba(255,255,255,0) 60%)` } : {}">
                <div class="na-temp-left">
                  <span class="na-temp-value">{{ hour.temperatura?.toFixed(0) || '--' }}<sup>°</sup>c</span>
                </div>
                <div class="na-temp-mid">
                  <span class="na-temp-info-label">máxima</span>
                  <b class="na-temp-info-value">{{ currentCidadeClima.temperatura_max?.toFixed(0) || '--' }}°c</b>
                  <span class="na-temp-info-label">mínima</span>
                  <span class="na-temp-info-value">{{ currentCidadeClima.temperatura_min?.toFixed(0) || '--' }}°c</span>
                </div>
                <div class="na-temp-right">
                  <span class="na-temp-info-label na-temp-umidade-label">
                    <img src="..\mapa\src\components\mapa\stationIcon\UmidadeBlack.svg" class="na-icon-14" alt="umidade" />
                    umidade
                  </span>
                  <b class="na-temp-info-value">{{ hour.umidade || '--' }}%</b>
                  <span class="na-temp-info-label">sensação</span>
                  <span class="na-temp-info-value">{{ hour.sensacao_termica?.toFixed(0) || '--' }}°c</span>
                </div>
              </div>
            </div>
            <!-- Chart/Table after Temperatura -->
            <div v-if="selectedCardKey === 'temperatura'" class="na-chart-table-card">
              <NewAsideHistoryChart :hours="currentDayData" :metricField="selectedChartMetric.field" :metricLabel="selectedChartMetric.label" :metricColor="selectedChartMetric.color" :chartType="selectedChartMetric.chartType || 'line'" :collapsed="historyCollapsed" @toggle-collapse="historyCollapsed = !historyCollapsed" />
              <NewAsideDataTable :hours="currentDayData" :columns="tableColumns" />
            </div>

            <!-- Vento -->
            <div :class="['na-section', 'na-wind-card', 'na-clickable', { 'na-card-selected': selectedCardKey === 'vento' }]"
              @click="selectCard('vento')">
              <div class="na-wind-header">
                <img src="..\mapa\src\components\mapa\stationIcon\VentoBlack.svg" class="na-icon-20" alt="vento" />
                <span>Vento</span>
              </div>
              <div class="na-wind-body">
                <div class="na-wind-col">
                  <span class="na-wind-label">velocidade</span>
                  <span class="na-wind-value-lg">{{ hour.vento_velocidade?.toFixed(0) || '0' }} km/h</span>
                </div>
                <div class="na-wind-col na-wind-col-center">
                  <div class="na-wind-dir-top">
                    <img
                      v-if="windDirIcon"
                      :src="windDirIcon"
                      class="na-icon-24" alt="dir"
                    />
                    <span class="na-wind-label">direção</span>
                  </div>
                  <span class="na-wind-value-lg">{{ windDirLabel }}</span>
                </div>
                <div class="na-wind-col">
                  <span class="na-wind-label">pressão</span>
                  <span class="na-wind-value-lg">{{ hour.pressao?.toFixed(0) || '--' }} hPa</span>
                </div>
              </div>
            </div>
            <!-- Chart/Table after Vento -->
            <div v-if="selectedCardKey === 'vento'" class="na-chart-table-card">
              <NewAsideHistoryChart :hours="currentDayData" :metricField="selectedChartMetric.field" :metricLabel="selectedChartMetric.label" :metricColor="selectedChartMetric.color" :chartType="selectedChartMetric.chartType || 'line'" :collapsed="historyCollapsed" @toggle-collapse="historyCollapsed = !historyCollapsed" />
              <NewAsideDataTable :hours="currentDayData" :columns="tableColumns" />
            </div>

            <!-- Metric cards with inline chart/table -->
            <template v-for="(row, rowIdx) in metricRows" :key="rowIdx">
              <div class="na-metrics-grid">
                <NewAsideMetricCard
                  v-for="card in row"
                  :key="card.metricKey"
                  v-bind="card"
                  :selected="selectedCardKey === card.metricKey"
                  @select="selectCard(card.metricKey)"
                />
              </div>
              <!-- Delta-T special: bar chart with colored bars + legend -->
              <div v-if="selectedCardKey === 'deltaT' && isRowSelected(row)" class="na-chart-table-card">
                <div class="na-deltat-viz">
                  <div class="na-deltat-viz-header" @click="deltaTCollapsed = !deltaTCollapsed">
                    <span class="na-deltat-viz-title">Gráfico Delta-T</span>
                    <font-awesome-icon
                      icon="fa fa-chevron-down"
                      :class="['na-arrow', { collapsed: deltaTCollapsed }]"
                    />
                  </div>
                  <div v-show="!deltaTCollapsed">
                    <div class="na-deltat-legend">
                      <div class="na-deltat-legend-item">
                        <div class="na-deltat-legend-cor" style="background:#5eb965"></div>
                        <span>Condições mais adequadas</span>
                      </div>
                      <div class="na-deltat-legend-item">
                        <div class="na-deltat-legend-cor" style="background:#fda700"></div>
                        <span>Inadequado para gotas médias, finas e muito finas</span>
                      </div>
                      <div class="na-deltat-legend-item">
                        <div class="na-deltat-legend-cor" style="background:#f9e862"></div>
                        <span>Condições limite</span>
                      </div>
                      <div class="na-deltat-legend-item">
                        <div class="na-deltat-legend-cor" style="background:#f45a51"></div>
                        <span>Condições não recomendadas</span>
                      </div>
                    </div>
                    <div class="na-deltat-chart-container">
                      <canvas ref="deltaTChart" :key="deltaTChartKey" width="310" height="175"></canvas>
                    </div>
                  </div>
                </div>
                <NewAsideDataTable :hours="currentDayData" :columns="deltaTTableColumns" />
              </div>

              <!-- Regular chart + table for other metrics -->
              <div v-else-if="isRowSelected(row)" class="na-chart-table-card">
                <NewAsideHistoryChart :hours="currentDayData" :metricField="selectedChartMetric.field" :metricLabel="selectedChartMetric.label" :metricColor="selectedChartMetric.color" :chartType="selectedChartMetric.chartType || 'line'" :collapsed="historyCollapsed" @toggle-collapse="historyCollapsed = !historyCollapsed" />
                <NewAsideDataTable :hours="currentDayData" :columns="tableColumns" />
              </div>
            </template>

            <!-- Last update info -->
            <div v-if="hour" class="na-update-info">
              <small>Última atualização: {{ new Date(hour.created).toLocaleString() }}</small>
            </div>
            <div class="na-update-info">
              <small>weatherapi</small>
            </div>
          </div>
        </div><!-- /na-days-container -->
      </div>
    </div>

    <!-- Loading -->
    <div v-if="load" class="text-center m-auto p-5">
      <Load color="#006035"></Load>
    </div>
  </div>
</template>

<script>
/* global Chart */
import Load from "../Load.vue";
import NewAsideDayItem from "./NewAsideDayItem.vue";
import NewAsideMetricCard from "./NewAsideMetricCard.vue";
import NewAsideHistoryChart from "./NewAsideHistoryChart.vue";
import NewAsideDataTable from "./NewAsideDataTable.vue";
import { moonPhaseInfo } from "./moonPhase";

const CHART_METRICS = {
  temperatura: { field: "temperatura", label: "Temperatura (°C)", color: "#F49C18", chartType: "line" },
  precipitacao: { field: "precipitacao", label: "Chuva (mm)", color: "#3B82F6", chartType: "bar" },
  vento_velocidade: { field: "vento_velocidade", label: "Vento (km/h)", color: "#727272", chartType: "line" },
  umidade: { field: "umidade", label: "Umidade (%)", color: "#3E97FF", chartType: "line" },
  pressao: { field: "pressao", label: "Pressão (hPa)", color: "#FF3E3E", chartType: "line" },
};

export default {
  name: "NewAsideClimate",
  components: { Load, NewAsideDayItem, NewAsideMetricCard, NewAsideHistoryChart, NewAsideDataTable },
  data() {
    return {
      info: null,
      hour: null,
      load: true,
      data: null,
      expandedDayIndex: null,
      selectedMetricKey: "temperatura",
      selectedCardKey: null,
      historyCollapsed: false,
      deltaTCollapsed: false,
      next: null,
      deltaTChartKey: 0,
    };
  },
  computed: {
    currentDayData() {
      if (!this.info || this.expandedDayIndex === null) return [];
      const data = this.info.days[this.expandedDayIndex]?.data || [];
      // No dia de HOJE, oculta as horas que já passaram (mostra da hora atual
      // em diante). Dias futuros são exibidos por completo. Backend já envia
      // ordenado; aqui só filtramos pelo horário atual.
      const now = new Date();
      const isToday = data.some((h) => {
        const d = new Date(h.data);
        return d.getFullYear() === now.getFullYear() &&
          d.getMonth() === now.getMonth() &&
          d.getDate() === now.getDate();
      });
      if (!isToday) return data;
      const currentHourStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), 0, 0, 0);
      return data.filter((h) => new Date(h.data) >= currentHourStart);
    },
    currentCidadeClima() { if (!this.info || this.expandedDayIndex === null) return {}; return this.info.days[this.expandedDayIndex]?.cidade_clima || {}; },
    currentDayAstronomia() {
      const cc = this.currentCidadeClima;
      if (!cc || (!cc.sol_nasce && !cc.sol_poe)) return null;
      return cc;
    },
    luaFaseInfo() {
      return moonPhaseInfo(this.currentCidadeClima?.lua_fase, this.currentCidadeClima?.lua_luminosidade);
    },
    luaFaseLabel() {
      return this.luaFaseInfo?.label || "";
    },
    luaFaseIcon() {
      return this.luaFaseInfo?.icon || null;
    },
    windDirMap() {
      return {
        N: "Norte", NNE: "Nordeste", NE: "Nordeste", ENE: "Leste",
        E: "Leste", ESE: "Suldeste", SE: "Suldeste", SSE: "Sul",
        S: "Sul", SSW: "Sudoeste", SW: "Sudoeste", WSW: "Oeste",
        W: "Oeste", WNW: "Noroeste", NW: "Noroeste", NNW: "Norte",
        L: "Leste", O: "Oeste", SO: "Sudoeste", NO: "Noroeste",
        Norte: "Norte", Nordeste: "Nordeste", Leste: "Leste", Suldeste: "Suldeste",
        Sul: "Sul", Sudoeste: "Sudoeste", Oeste: "Oeste", Noroeste: "Noroeste"
      };
    },
    windDirLabel() {
      if (!this.hour || !this.hour.vento_vetorial) return '--';
      return this.windDirMap[this.hour.vento_vetorial] || this.hour.vento_vetorial;
    },
    windDirIcon() {
      if (!this.hour || !this.hour.vento_vetorial) return null;
      const dir = this.windDirMap[this.hour.vento_vetorial];
      if (!dir) return null;
      try { return require(`../mapa/src/components/mapa/stationIcon/direcao/${dir}.svg`); } catch { return null; }
    },
    expandedContentRadius() {
      if (!this.info || this.expandedDayIndex === null) return {};
      const isFirst = this.expandedDayIndex === 0;
      const isLast = this.expandedDayIndex === this.info.days.length - 1;
      const tl = isFirst ? '0' : '10px';
      const tr = isLast ? '0' : '10px';
      return { borderRadius: `${tl} ${tr} 10px 10px` };
    },
    selectedChartMetric() { return CHART_METRICS[this.selectedMetricKey] || CHART_METRICS.temperatura; },
    metricsList() {
      if (!this.hour) return [];
      const h = this.hour;
      const deltaT = this.calculateDeltaT(h);
      return [
        { metricKey: "deltaT", label: "Delta T", value: deltaT != null ? deltaT : "N/D", unit: deltaT != null ? this.getAdequado(deltaT) : "", colorBar: true, iconSrc: require("../mapa/src/components/mapa/stationIcon/deltat/IDeltaT.svg"), darkIcon: true, gradient: `radial-gradient(ellipse at 0% 0%, ${this.getDeltaTColor()}1F 0%, rgba(255,255,255,0.12) 100%)` },
        { metricKey: "precipitacao", label: "Chuva\nacumulada", value: this.currentCidadeClima.precipitacao_total?.toFixed(1) ?? "0", unit: "mm", iconSrc: require("../mapa/src/components/mapa/stationIcon/ChuvaBlack.svg"), gradient: "radial-gradient(ellipse at 0% 0%, rgba(0, 132, 255, 0.12) 0%, rgba(255, 255, 255, 0.12) 100%)" },
      ];
    },
    metricRows() {
      const metrics = this.metricsList;
      const rows = [];
      for (let i = 0; i < metrics.length; i += 2) {
        const row = [metrics[i]];
        if (i + 1 < metrics.length) row.push(metrics[i + 1]);
        rows.push(row);
      }
      return rows;
    },
    deltaTTableColumns() {
      return [
        { key: "hora", label: "Hora", format: "time", width: "20%" },
        { key: "temp", label: "Temp (°C)", formatter: (row) => row.temperatura?.toFixed(1) ?? "--" },
        { key: "umid", label: "Umid (%)", formatter: (row) => row.umidade ?? "--" },
        { key: "vento", label: "Vento (km/h)", formatter: (row) => row.vento_velocidade?.toFixed(0) ?? "--" },
        { key: "dir", label: "Direção", formatter: (row) => this.windDirMap[row.vento_vetorial] || row.vento_vetorial || "--" },
      ];
    },
    tableColumns() {
      return [
        { key: "hora", label: "Hora", format: "time", width: "22%" },
        { key: "temp", label: "Temp (°C)", formatter: (row) => row.temperatura?.toFixed(1) ?? "--" },
        { key: "chuva", label: "Chuva (mm)", formatter: (row) => row.precipitacao?.toFixed(1) ?? "--" },
        { key: "vento", label: "Vento (km/h)", formatter: (row) => row.vento_velocidade?.toFixed(0) ?? "--" },
        { key: "umid", label: "Umid (%)", formatter: (row) => row.umidade ?? "--" },
      ];
    },
  },
  methods: {
    formatDate(dt) { if (!dt) return "--"; return new Date(dt).toLocaleDateString("pt-BR"); },
    formatTime(dt) { if (!dt) return "--"; return new Date(dt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }); },
    formatHourMinute(t) {
      if (!t) return "--";
      const s = String(t);
      const match = s.match(/(\d{1,2}):(\d{2})/);
      return match ? `${match[1].padStart(2, "0")}:${match[2]}` : s;
    },
    calculateDeltaT(hourData) { if (!hourData) return null; const { temperatura, umidade } = hourData; if (temperatura == null || umidade == null) return null; const po = temperatura * Math.atan(0.151977 * Math.sqrt(umidade + 8.313659)) + Math.atan(temperatura + umidade) - Math.atan(umidade - 1.676331) + 0.00391838 * Math.pow(umidade, 1.5) * Math.atan(0.023101 * umidade) - 4.686035; return Number((temperatura - po).toFixed(2)); },
    getAdequado(deltaT) { if (deltaT < 1) return "Ruim"; if (deltaT < 2) return "Regular"; if (deltaT < 10) return "Adequado"; if (deltaT < 12) return "Inadequado"; return "Ruim"; },
    getTemperatureColor() {
      if (!this.hour || this.hour.temperatura == null) return "128,128,128";
      const valor = this.hour.temperatura;
      let r, g, b;
      if (valor <= -30) { r = 149; g = 185; b = 217; }
      else if (valor <= -15) { const p = ((valor + 30) * 100) / 15; r = Math.round(149 - (149 * p) / 100); g = Math.round(185 - (104 * p) / 100); b = Math.round(217 - (44 * p) / 100); }
      else if (valor <= 5) { const p = ((valor + 15) * 100) / 20; r = 0; g = Math.round(81 + (127 * p) / 100); b = Math.round(173 - (85 * p) / 100); }
      else if (valor <= 15) { const p = ((valor - 5) * 100) / 10; r = Math.round(255 * p / 100); g = Math.round(208 - (73 * p) / 100); b = Math.round(88 - (88 * p) / 100); }
      else if (valor <= 30) { const p = ((valor - 15) * 100) / 15; r = Math.round(255 - (7 * p) / 100); g = Math.round(135 - (135 * p) / 100); b = 0; }
      else if (valor <= 40) { const p = ((valor - 30) * 100) / 10; r = Math.round(248 - (92 * p) / 100); g = 0; b = 0; }
      else if (valor <= 50) { const p = ((valor - 40) * 100) / 10; r = Math.round(156 - (31 * p) / 100); g = 0; b = 0; }
      else { r = 125; g = 0; b = 0; }
      return `${r},${g},${b}`;
    },
    getDeltaTColor() {
      if (!this.hour) return "#727272";
      const dt = this.calculateDeltaT(this.hour);
      if (dt == null) return "#727272";
      const status = this.getAdequado(dt);
      switch(status) { case "Ruim": return "#f45a51"; case "Regular": return "#f9e862"; case "Inadequado": return "#fda700"; case "Adequado": return "#5eb965"; default: return "#727272"; }
    },
    onDaySelect(index) { this.expandedDayIndex = index; },
    getCorDeltaT(deltaT) {
      const status = this.getAdequado(deltaT);
      switch(status) { case "Ruim": return "#f45a51"; case "Regular": return "#f9e862"; case "Inadequado": return "#fda700"; case "Adequado": return "#5eb965"; default: return "#727272"; }
    },
    renderDeltaTChart() {
      // Increment key so Vue creates a brand-new canvas element,
      // guaranteeing a fresh context on every browser/webview.
      this.deltaTChartKey++;
      this.$nextTick(() => {
        const canvas = this.$refs.deltaTChart;
        if (!canvas || !this.currentDayData.length) return;
        const labels = [];
        const data = [];
        const bgColors = [];
        for (const h of this.currentDayData) {
          labels.push(new Date(h.data).toLocaleTimeString("pt-BR").substring(0, 5));
          const dt = this.calculateDeltaT(h);
          data.push(dt);
          bgColors.push(dt !== null ? this.getCorDeltaT(dt) : "#727272");
        }
        new Chart(canvas, {
          type: "bar",
          data: { labels, datasets: [{ label: "Delta-T", data, backgroundColor: bgColors, borderColor: bgColors, borderWidth: 1 }] },
          options: {
            animation: { duration: 300 },
            responsive: true,
            maintainAspectRatio: false,
            legend: { display: false },
            tooltips: { backgroundColor: "rgba(255,255,255,0.95)", titleFontColor: "#333", bodyFontColor: "#333", borderColor: "#ddd", borderWidth: 1, cornerRadius: 8 },
            scales: {
              yAxes: [{ ticks: { beginAtZero: true, fontColor: "#999", fontSize: 10 }, gridLines: { color: "#e8e8e8" } }],
              xAxes: [{ ticks: { fontColor: "#999", fontSize: 9, maxRotation: 0 }, gridLines: { display: false } }]
            }
          }
        });
      });
    },
    selectCard(cardKey) {
      const chartMap = { temperatura: "temperatura", vento: "vento_velocidade", deltaT: "temperatura", vento_vetorial: "vento_velocidade", pressao: "pressao", precipitacao: "precipitacao" };
      if (this.selectedCardKey === cardKey) {
        this.selectedCardKey = null;
      } else {
        this.selectedCardKey = cardKey;
        this.selectedMetricKey = chartMap[cardKey] || "temperatura";
        if (cardKey === "deltaT") {
          this.renderDeltaTChart();
        }
      }
    },
    isRowSelected(row) {
      return row.some(card => card.metricKey === this.selectedCardKey);
    },
    updateHour() { if (!this.info || this.expandedDayIndex === null) return; const dayData = this.currentDayData; if (!dayData.length) return; this.hour = dayData[0]; },
    getDataFirstRender(cidadeId, data) { this.getData(cidadeId, data); },
    getData(cidadeId, data) { clearTimeout(this.next); this.data = data; this.load = true; this.next = setTimeout(() => { this.info = null; this.expandedDayIndex = null; this.urlGet("/WeatherForecast/GetWeatherForecastById/" + cidadeId).then((e) => { this.info = e.data; this.$emit("info", this.info); const now = new Date(data).getDate(); for (let i = 0; i < this.info.days.length; i++) { const d = new Date(this.info.days[i].cidade_clima.data).getDate(); if (d === now) { this.expandedDayIndex = i; break; } } if (this.expandedDayIndex === null) { this.expandedDayIndex = 0; } }).catch(() => {}).finally(() => { this.load = false; }); }, 300); },
  },
  watch: {
    expandedDayIndex(val) {
      this.selectedCardKey = null;
      if (val !== null) { this.updateHour(); }
    },
  },
};
</script>

<style scoped>
/* General */
.na-section {
  background: #fff;
  border-radius: 10px;
  padding: 10px;
}

.na-historico-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.na-historico-title {
  font-weight: 600;
  font-size: 14px;
  color: #333;
}

/* Days container */
.na-days-container {
  position: relative;
}

.na-days-row {
  display: flex;
  overflow-x: auto;
  gap: 3px;
  padding: 0;
  position: relative;
  z-index: 2;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.na-days-row::-webkit-scrollbar {
  display: none;
}

.na-expanded-content {
  border: 1px solid #C3D9AD;
  border-radius: 10px;
  background: #FFFFFF80;
  padding: 10px;
  position: relative;
  z-index: 1;
  margin-top: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  animation: na-expand 0.3s ease-out;
}

@keyframes na-expand {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Astronomia */
.na-astronomia {
  padding: 8px 10px;
}

.na-astro-label {
  font-size: 12px;
  color: #333;
  margin-bottom: 8px;
}

.na-astro-row {
  display: flex;
  justify-content: space-between;
}

.na-astro-item {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.na-astro-sun {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255,240,0,0.8) 30%, rgba(255,239,66,0.5) 60%, rgba(255,248,0,0) 100%);
  flex-shrink: 0;
}

.na-astro-moon {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255,255,255,0.95) 40%, rgba(234,234,234,0.6) 100%);
  border: 1px solid #eaeaea;
  flex-shrink: 0;
}
img.na-astro-moon {
  background: none;
  border: none;
  border-radius: 0;
  object-fit: contain;
}

.na-astro-info {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  color: #333;
  line-height: 1.3;
}

.na-astro-info b { font-size: 12px; color: #333; }
.na-astro-info span { font-size: 11px; color: #666; }

/* Leitura */
.na-leitura {
  padding: 6px 12px;
}

.na-leitura-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.na-leitura-label { font-weight: 600; font-size: 12px; color: #333; }
.na-leitura-date { font-size: 12px; color: #333; }
.na-leitura-time { font-size: 12px; color: #006035; }

/* Clickable & Selected */
.na-clickable { cursor: pointer; transition: border-color 0.2s, background-color 0.2s; }
.na-card-selected { background: #EFF3FF !important; border: 1px solid #BBD1FF !important; }

.na-chart-table-card {
  background: #EFF3FF;
  border: 1px solid #BBD1FF;
  border-radius: 10px;
  padding: 10px;
}

/* Temperatura card */
.na-temp-card { padding: 10px; }

.na-temp-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  font-size: 12px;
  color: #333;
}

.na-temp-header img {
  filter: brightness(0);
}

.na-temp-body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 8px;
  padding: 8px;
}

.na-temp-left { flex-shrink: 0; }

.na-temp-value {
  font-size: 48px;
  font-weight: 700;
  color: #333;
  line-height: 1;
}

.na-temp-value sup { font-size: 28px; }

.na-temp-mid {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 10px;
}

.na-temp-right {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.na-temp-umidade-label {
  display: flex;
  align-items: center;
  gap: 3px;
}

.na-temp-info-label { font-size: 12px; color: #999; line-height: 20px; }
.na-temp-info-value { font-size: 14px; font-weight: 700; color: #333; line-height: 20px; }

/* Wind card */
.na-wind-card { padding: 10px; }

.na-wind-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  font-size: 12px;
  color: #333;
}

.na-wind-header img {
  filter: brightness(0);
}

.na-wind-body { display: flex; align-items: stretch; }

.na-wind-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 3px;
}

.na-wind-col-center { border-left: 1px solid #eee; border-right: 1px solid #eee; }
.na-wind-dir-top { display: flex; align-items: center; gap: 4px; justify-content: center; min-height: 24px; }
.na-wind-label { font-size: 13px; color: #666; line-height: 24px; }
.na-wind-value-lg { font-size: 18px; font-weight: 700; color: #333; text-align: center; line-height: 22px; }

/* Metrics Grid */
.na-metrics-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5px;
}

/* Common icons */
.na-icon-20 { width: 20px; height: 20px; object-fit: contain; }
.na-icon-24 { width: 24px; height: 24px; object-fit: contain; }
.na-icon-14 { width: 14px; height: 14px; object-fit: contain; }

/* Delta-T legend and chart */
.na-deltat-viz {
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 8px;
}

.na-deltat-viz > div:last-child {
  padding: 0 12px 12px;
}

.na-deltat-viz-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  user-select: none;
}

.na-deltat-viz-title {
  font-weight: 600;
  font-size: 14px;
  color: #333;
}

.na-deltat-viz-header .na-arrow {
  font-size: 12px;
  color: #666;
  transition: transform 0.3s;
}

.na-deltat-viz-header .na-arrow.collapsed {
  transform: rotate(-90deg);
}

.na-deltat-legend {
  margin-bottom: 10px;
  padding: 5px;
}

.na-deltat-legend-item {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.na-deltat-legend-cor {
  width: 16px;
  height: 16px;
  min-width: 16px;
  flex-shrink: 0;
  margin-right: 8px;
  border-radius: 4px;
}

.na-deltat-legend-item span {
  font-size: 11px;
  color: #333;
  text-align: start;
}

.na-deltat-chart-container {
  position: relative;
  height: 175px;
  margin-bottom: 8px;
}

/* Update info */
.na-update-info { text-align: center; color: #006035; padding: 4px; }
.na-update-info small { font-size: 11px; }

.na-section-title {
  font-weight: 600;
  font-size: 13px;
  color: #333;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}
</style>
